import React, { useEffect, useState, useRef } from 'react'
import AWS from 'aws-sdk'
import Split from 'react-split'
import { API } from '@aws-amplify/api'
import OpenSeadragon from '../openseadragon'
import Loader from '../openSeadragonUtils/Loader'
import { WOMA_API_GATEWAY_NAME } from '../../../constants/aws'
import { Button } from '@mui/material'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import RefreshIcon from '@mui/icons-material/Refresh'
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined'
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined'
import ImageFilter from './ImageFilter'
import { ViewerError } from './ViewerError'
import Tooltip from '@mui/material/Tooltip'
import CancelIcon from '@mui/icons-material/Cancel'
import { useDispatch, useSelector } from 'react-redux'

import { viewerBarcodeOptions, viewerClassifierOptions } from '../openSeadragonUtils/initOptions'
import { setShowingRectangle } from '../../../actions'
// import { toggleAllPremerisRects } from '../openSeadragonUtils/uiUtils'
import {
  makeClassifierAddToSummaryOverlay,
  makePremerisMovieOverlay,
  makeWholeSlideOverlay,
} from '../openSeadragonUtils/overlayHelper'
import Scalebar from '../openSeadragonUtils/openseadragonScalebar'
import { auditClassifierAccessed } from '../../../actions'

export default function PremerisImage({
  isShow,
  setShow,
  trfBarcode,
  stain,
  currentSlideBarcode,
  imgFilterValue,
  classifier,
  setImgFilterValue,
  currentLocation,
  user,
  casenumber,
}) {
  const dispatch = useDispatch()
  const barcodeData = useSelector((state) => state.addBarcodeData).data
  const bucket = barcodeData?.bucket || ''
  const kpInstance = barcodeData?.kpInstance || ''
  const [viewerBarcode, setViewerBarcode] = useState(null)
  const [viewerClassifier, setViewerClassifier] = useState(null)
  const [viewerBarcodeLoading, setViewerBarcodeLoading] = useState(false)
  const [viewerClassifierLoading, setViewerClassifierLoading] = useState(false)
  const [premerisOverlays, setPremerisOverlays] = useState({})
  const [allPremerisOverlays, setAllPremerisOverlays] = useState({})
  const [wholeSlideRects, setWholeSlideRects] = useState([])
  const [premerisRectOrder, setPremerisRectOrder] = useState([])
  const [tilesLength, setTilesLength] = useState(0)
  const [premerisRectLocationsFetched, setPremerisRectLocationsFetched] = useState(false)
  const [splitSizes, setSplitSizes] = useState([50, 50])
  const [isBarcodePopulated, setBarcodePopulated] = useState(false)
  const [isClassifierPopulated, setClassifierPopulated] = useState(false)
  const [isRefreshClicked, setRefreshClicked] = useState(false)
  const [prevBarcodeZoom, setPrevBarcodeZoom] = useState(0)
  const [prevClassifierZoom, setPrevClassifierZoom] = useState(0)
  const [prevBarcodePoint, setPrevBarcodePoint] = useState({ x: 0, y: 0 })
  const [prevClassifierPoint, setPrevClassifierPoint] = useState({ x: 0, y: 0 })
  const [maxOptimalZoom, setMaxOptimalZoom] = useState(0)
  const [homeZoom, setHomeZoom] = useState(0)
  const [eventObject, setEventObject] = useState(null)
  const [dziHighestLevel, setDziHighestLevel] = useState(0)
  const [isCheckingStatus, setIsCheckingStatus] = useState(false)
  const [filesChecked, setFilesChecked] = useState(false)
  const [viewerData, setViewerData] = useState({
    bucket: '',
    kpInstance: '',
    classifierKey: null,
    classifierUrl: null,
    wsiKey: null,
    wsiUrl: null,
    classifierFileExists: false,
    wsiFileExists: false,
    classifierError: null,
    wsiError: null,
  })

  // Add refs to track viewer instances for proper cleanup
  const viewerBarcodeRef = useRef(null)
  const viewerClassifierRef = useRef(null)
  const cleanupTimeoutRef = useRef(null)
  const debounceTimeoutRef = useRef(null)
  const lastStainChangeRef = useRef('')

  const s3 = new AWS.S3()
  window.s3 = s3

  // Use barcodeData prop directly with updated fallback structure
  const effectiveBarcodeData = barcodeData || {
    bucket: '',
    kpInstance: '',
    versions: [],
    classifiersInfo: [],
  }

  // First try to find the default classifier, then fall back to first valid classifier
  const effectiveClassifierResponsefilter =
    (effectiveBarcodeData?.classifiersInfo || []).filter((classifierItem) => {
      if (classifier === 'default') {
        return classifierItem.version === 'default' && classifierItem.tilesKey && classifierItem.wsiKey
      }
      return classifierItem.version === classifier && classifierItem.tilesKey && classifierItem.wsiKey
    }) || []

  // Enhanced cleanup function to properly destroy viewers
  const destroyViewers = () => {
    // Clear any existing cleanup timeout
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current)
      cleanupTimeoutRef.current = null
    }

    // Destroy barcode viewer
    if (viewerBarcodeRef.current) {
      try {
        viewerBarcodeRef.current.destroy()
      } catch (error) {
        console.error('Error destroying barcode viewer:', error)
      }
      viewerBarcodeRef.current = null
    }

    // Destroy classifier viewer
    if (viewerClassifierRef.current) {
      try {
        viewerClassifierRef.current.destroy()
      } catch (error) {
        console.error('Error destroying classifier viewer:', error)
      }
      viewerClassifierRef.current = null
    }

    // Clear DOM containers
    document.querySelectorAll('div#viewerBarcode .openseadragon-container').forEach((e) => {
      e.remove()
    })
    document.querySelectorAll('div#viewerClassifier .openseadragon-container').forEach((e) => {
      e.remove()
    })

    // Reset state
    setViewerBarcode(null)
    setViewerClassifier(null)
    setViewerBarcodeLoading(false)
    setViewerClassifierLoading(false)
    setBarcodePopulated(false)
    setClassifierPopulated(false)
  }

  // Enhanced useEffect to handle stain/classifier changes with proper cleanup and debouncing
  useEffect(() => {
    const stainKey = `${stain}-${classifier}-${currentSlideBarcode}`

    // Clear any existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    if (currentSlideBarcode && classifier && lastStainChangeRef.current !== stainKey) {
      // Debounce the change to prevent rapid switching
      debounceTimeoutRef.current = setTimeout(() => {
        // Destroy existing viewers
        destroyViewers()

        // Reset viewer data
        setViewerData({
          bucket: '',
          kpInstance: '',
          classifierKey: null,
          classifierUrl: null,
          wsiKey: null,
          wsiUrl: null,
          classifierFileExists: false,
          wsiFileExists: false,
          classifierError: null,
          wsiError: null,
        })

        setRefreshClicked(false)
        setFilesChecked(false)
        lastStainChangeRef.current = stainKey
      }, 300) // 300ms debounce delay
    }
  }, [classifier, currentSlideBarcode, stain])

  // Check file existence before creating viewers
  useEffect(() => {
    if (currentSlideBarcode && !filesChecked) {
      checkFilesExist()
    }
  }, [currentSlideBarcode, filesChecked])

  // Only create viewers if files exist
  useEffect(() => {
    if (filesChecked && viewerData.wsiFileExists && !viewerBarcode) {
      createBarcodeViewer()
      setBarcodePopulated(true)
    }
  }, [filesChecked, viewerData.wsiFileExists, viewerBarcode])

  useEffect(() => {
    if (filesChecked && viewerData.classifierFileExists && !viewerClassifier) {
      createClassifierViewer()
      setClassifierPopulated(true)
    }
  }, [filesChecked, viewerData.classifierFileExists, viewerClassifier])

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current)
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      destroyViewers()
    }
  }, [])

  const checkFilesExist = async () => {
    if (!effectiveClassifierResponsefilter[0] || !effectiveBarcodeData.kpInstance) {
      setViewerData({
        ...viewerData,
        classifierError: {
          message: 'Sorry, the image is not available.',
        },
        wsiError: {
          message: 'Sorry, the image is not available.',
        },
      })
      setFilesChecked(true)
      return
    }

    const wsiFileParams = {
      Bucket: effectiveBarcodeData.bucket,
      Key: effectiveClassifierResponsefilter[0].wsiKey,
    }
    const classifierFileParams = {
      Bucket: effectiveBarcodeData.bucket,
      Key: effectiveClassifierResponsefilter[0].tilesKey,
    }

    try {
      const wsiUrl = s3.getSignedUrl('getObject', wsiFileParams)
      const classifierUrl = s3.getSignedUrl('getObject', classifierFileParams)

      // Check if classifier image exists first
      const classifierExists = await checkIfFileExists(classifierUrl)
      const wsiExists = await checkIfFileExists(wsiUrl)

      if (!classifierExists) {
        setViewerData({
          ...viewerData,
          classifierError: {
            message: 'Sorry, the image is not available.',
          },
        })
        // Clear classifier viewer if it exists
        if (viewerClassifier) {
          viewerClassifier.destroy()
          setViewerClassifier(null)
        }
      }

      if (!wsiExists) {
        setViewerData({
          ...viewerData,
          wsiError: {
            message: 'No Image Available',
          },
        })
        // Clear barcode viewer if it exists
        if (viewerBarcode) {
          viewerBarcode.destroy()
          setViewerBarcode(null)
        }
      }

      setFilesChecked(true)
      setViewerData({
        bucket: effectiveBarcodeData.bucket,
        kpInstance: effectiveBarcodeData.kpInstance,
        classifierKey: effectiveClassifierResponsefilter[0].tilesKey,
        classifierUrl: classifierUrl,
        wsiKey: effectiveClassifierResponsefilter[0].wsiKey,
        wsiUrl: wsiUrl,
        classifierFileExists: classifierExists,
        wsiFileExists: wsiExists,
      })
    } catch (error) {
      console.error('Error checking files:', error)
      setFilesChecked(true)
    }
  }

  // useEffect(() => {
  //   if (Object.keys(premerisOverlays).length) {
  //     handleToggleAllPremerisRects()
  //   }
  // }, [isShow, premerisOverlays])

  // Helper function to store data in local storage
  const saveToLocalStorage = (stainName, data) => {
    const stainsData = getFromLocalStorage() || {}
    stainsData[stainName] = data
    localStorage.setItem('stains_data', JSON.stringify(stainsData))
  }

  // Helper function to retrieve data from local storage
  const getFromLocalStorage = () => {
    const data = localStorage.getItem('stains_data')
    return data ? JSON.parse(data) : {}
  }

  useEffect(() => {
    if (Object.keys(imgFilterValue).length > 0) {
      // Filters
      handleStyleImageFilters() //Trigger only Style filters Brightness, Contrast and Sharpness
      //localStorage.setItem('filters', JSON.stringify(imgFilterValue))
      saveToLocalStorage(stain.toLowerCase(), imgFilterValue)
    }
  }, [imgFilterValue.brightness, imgFilterValue.contrast])

  useEffect(() => {
    if (!JSON.parse(localStorage.getItem('stains_data'))[stain.toLowerCase()]) {
      saveToLocalStorage(stain.toLowerCase(), {
        brightness: 100,
        contrast: 100,
        sharpness: 0,
        gammaR: 100,
        gammaG: 100,
        gammaB: 100,
        sync: true,
      })
    }
  }, [stain])

  useEffect(() => {
    if (Object.keys(imgFilterValue).length > 0) {
      // Gamma Filters')
      handleGammaImageFilters() //Trigger only Gamma RGB
      saveToLocalStorage(stain.toLowerCase(), imgFilterValue)
      //localStorage.setItem('filters', JSON.stringify(imgFilterValue))
    }
  }, [imgFilterValue.gammaR, imgFilterValue.gammaG, imgFilterValue.gammaB, imgFilterValue.sharpness])

  useEffect(() => {
    if (currentSlideBarcode && viewerBarcode != null) {
      // Fetch Regions
      getPremerisData()
    }
  }, [currentSlideBarcode, viewerBarcode]) //isShow

  // Remove the useEffect that creates viewers unconditionally
  useEffect(() => {
    // Create OSD viewers
    if (currentSlideBarcode) {
      // Viewer Null
      setViewerBarcode(null)
      setViewerClassifier(null)
    }
  }, [currentSlideBarcode])

  const handleRefreshClick = (e) => {
    // Save current viewer state before destroying
    if (viewerClassifier && viewerClassifier.viewport) {
      setPrevClassifierZoom(viewerClassifier.viewport.getZoom())
      setPrevBarcodeZoom(viewerBarcode.viewport.getZoom())
      setPrevClassifierPoint(viewerClassifier.viewport.getBounds(true).getCenter())
    }
    if (viewerBarcode && viewerBarcode.viewport) {
      setPrevBarcodePoint(viewerBarcode.viewport.getBounds(true).getCenter())
    }

    destroyViewers()
    setRefreshClicked(true)
  }

  // Separate useEffect to handle adding handlers when viewers are created
  useEffect(() => {
    // Add handlers to viewer_barcode when it's created and file exists
    if (viewerBarcode && viewerData.wsiFileExists) {
      addViewerBarcodeHandlers()
    }

    // Add handlers to viewer_classifier when it's created and file exists
    if (viewerClassifier && viewerData.classifierFileExists) {
      addViewerClassifierHandlers()
    }
  }, [viewerBarcode, viewerClassifier, viewerData.wsiFileExists, viewerData.classifierFileExists])

  useEffect(() => {
    if (isBarcodePopulated || (isClassifierPopulated && currentSlideBarcode)) {
      getSlideBarcodeData()

      // Reset populated flags since we handle handlers separately
      if (isBarcodePopulated) {
        setBarcodePopulated(false)
      }
      if (isClassifierPopulated) {
        setClassifierPopulated(false)
      }

      // Add null checks before accessing parentElement properties
      if (viewerClassifier && viewerClassifier.navigator && viewerClassifier.navigator.container) {
        const classifierElement = viewerClassifier.navigator.container.parentElement
        if (classifierElement && classifierElement.parentElement && classifierElement.parentElement.parentElement) {
          classifierElement.parentElement.parentElement.id = 'classifier'
        }
      }

      if (viewerBarcode && viewerBarcode.navigator && viewerBarcode.navigator.container) {
        const barcodeElement = viewerBarcode.navigator.container.parentElement
        if (barcodeElement && barcodeElement.parentElement && barcodeElement.parentElement.parentElement) {
          barcodeElement.parentElement.parentElement.id = 'barcode'
        }
      }

      // Add document event listener for space, b, etc.
      addDocumentEventListeners()
      // Configure custom splitJS gutters
      configureCustomSplitGutters()
    }
  }, [isBarcodePopulated, isClassifierPopulated, currentSlideBarcode, viewerBarcode, viewerClassifier])

  /**
   * @function addDocumentEventListeners
   * @desc adds event listeners to document for keyup
   * @returns void
   */
  const addDocumentEventListeners = () => {
    document.body.addEventListener('keyup', documentKeyupListener, true)
  }

  /**
   * @function documentKeyupListener
   * @desc Handles the keyup event added to the document
   * @param e keyup event.
   * @returns {void}
   */
  const documentKeyupListener = (e) => {
    /* if (e.code === 'Space') {
      if (
        document.body.querySelector('button#show') &&
        document.body.querySelector('button#show').textContent === 'Show Regions'
      ) {
        setShow(true)
      } else if (
        document.body.querySelector('button#hide') &&
        document.body.querySelector('button#hide').textContent === 'Hide Regions'
      ) {
        setShow(false)
      }
    } */
  }

  /**
   * @function createOSDviewers
   * @desc Instantiates 3 instances of OSD - classifier, barcode, and selections helper viewer
   * @returns {void}
   */
  const createBarcodeViewer = () => {
    document.querySelectorAll('div#viewerBarcode .openseadragon-container').forEach((e) => e.remove())
    setViewerBarcodeLoading(true)
    viewerBarcodeOptions.bucket = bucket
    viewerBarcodeOptions.s3 = s3

    const newViewer = OpenSeadragon(viewerBarcodeOptions)
    viewerBarcodeRef.current = newViewer
    setViewerBarcode(newViewer)
  }

  /**
   * @function createOSDviewers
   * @desc Instantiates 3 instances of OSD - classifier, barcode, and selections helper viewer
   * @returns {void}
   */
  const createClassifierViewer = () => {
    document.querySelectorAll('div#viewerClassifier .openseadragon-container').forEach((e) => e.remove())
    setViewerClassifierLoading(true)
    viewerClassifierOptions.bucket = bucket
    viewerClassifierOptions.s3 = s3

    const newViewer = OpenSeadragon(viewerClassifierOptions)
    viewerClassifierRef.current = newViewer
    setViewerClassifier(newViewer)
  }

  const checkIfFileExists = async (url) => {
    try {
      const response = await fetch(url, { method: 'GET' })
      return response.status === 200
    } catch (error) {
      console.error('Error checking file:', error)
      return false
    }
  }

  const getSlideBarcodeData = async () => {
    const slideBarcode = currentSlideBarcode

    // Find the classifier info for the selected version
    try {
      dispatch(
        auditClassifierAccessed({
          userId: user,
          case_number: casenumber,
          location: currentLocation,
          stain: currentSlideBarcode,
          img_version: effectiveClassifierResponsefilter[0].version,
          img_url: viewerData.wsiKey,
          img_type: 'wsi',
          success: viewerData.wsiFileExists,
        }),
      )
      dispatch(
        auditClassifierAccessed({
          userId: user,
          case_number: casenumber,
          location: currentLocation,
          stain: currentSlideBarcode,
          img_version: effectiveClassifierResponsefilter[0].version,
          img_url: viewerData.classifierKey,
          img_type: 'classifier',
          success: viewerData.classifierFileExists,
        }),
      )

      if (viewerData.classifierFileExists && viewerClassifier) {
        viewerClassifier.open(viewerData.classifierUrl)
      }
      if (viewerData.wsiFileExists && viewerBarcode) {
        viewerBarcode.open(viewerData.wsiUrl)
      }
    } catch (error) {
      console.error('Error loading image data:', error)
    }
  }

  /**
   * @function getPremerisData
   * @desc Fetch requests to get:
   * 1) Premeris rect locations on Premeris View
   * 2) Premeris rect locations on whole slide
   * This data is needed to init Premeris Movie and Grid Overlay
   * @returns {void}
   */

  const getPremerisData = async () => {
    const slideBarcode = currentSlideBarcode
    if (!effectiveClassifierResponsefilter[0] || !effectiveBarcodeData.kpInstance) {
      console.error('Missing required data for premeris data fetch')
      return
    }

    const fetchPremerisRegions = (json) => {
      const { regResult, regWholeSlideRects } = json
      if (regResult && regResult.premerisRectOrder && regResult.premerisRectLocationsFetched) {
        setPremerisRectOrder(regResult.premerisRectOrder)
        setTilesLength(regResult.premerisRectOrder.length)
        setPremerisRectLocationsFetched(regResult.premerisRectLocationsFetched)
      }
      setWholeSlideRects(regWholeSlideRects)
      const overlays = {}
      regWholeSlideRects.forEach((rect) => {
        const osdRect = viewerBarcode.viewport.imageToViewportRectangle(
          rect.slideRect.xmin,
          rect.slideRect.ymin,
          rect.slideRect.width,
          rect.slideRect.height,
        )
        overlays[rect.clid] = osdRect
      })
      if (Object.keys(overlays).length) {
        setAllPremerisOverlays(overlays)
      }
    }

    try {
      const apiName = '/premeris/slideBarcodeInfo/premerisRectLocations'
      const options = {
        headers: {},
        response: false,
      }
      const premerisTablesData = await API.get(
        WOMA_API_GATEWAY_NAME,
        `${apiName}/${slideBarcode}/${effectiveBarcodeData.kpInstance}/${effectiveClassifierResponsefilter[0].version}`,
        options,
      )
      await fetchPremerisRegions(premerisTablesData)
    } catch (error) {
      console.error('Error fetching premeris data:', error)
    }
  }

  /**
   * @function addViewerBarcodeHandlers
   * @desc Adds event handlers to viewer_barcode
   * @returns {void}
   */
  const addViewerBarcodeHandlers = () => {
    // Add null check at the beginning
    if (!viewerBarcode) {
      console.error('Cannot add barcode handlers: viewerBarcode is null')
      return
    }

    // On viewer_barcode open failed
    viewerBarcode.addHandler('open-failed', (e) => {
      setViewerBarcodeLoading(false)
      // Set error message if open fails
      setViewerData({
        ...viewerData,
        wsiError: {
          message: 'Sorry, the image is not available.',
          description: '',
        },
      })
    })

    // On viewer_barcode open
    viewerBarcode.addHandler('open', () => {
      setViewerBarcodeLoading(false)
      // Only clear error if we know file exists
      if (viewerData.wsiFileExists) {
        setViewerData({
          ...viewerData,
          wsiError: null,
        })
      }

      if (isRefreshClicked) {
        viewerBarcode.viewport.zoomTo(prevBarcodeZoom)
        viewerBarcode.viewport.panTo({ x: prevBarcodePoint.x, y: prevBarcodePoint.y })
      } else {
        const center = viewerBarcode.viewport.getBounds(true).getCenter()
        viewerBarcode.viewport.panTo({ x: center.x, y: center.y })
      }
      // create all premeri overlays s tilesource has loaded in viewer
      if (Object.keys(premerisOverlays).length !== wholeSlideRects.length) {
        createPremerisOverlays()
      }

      // set minLevel to 8 - unsure if we want this
      const image = viewerBarcode.world.getItemAt(0)
      image.source.tileWidth = '100%'
      image.source.tileHeight = '100%'
    })

    // On viewer_barcode update-level
    viewerBarcode.addHandler('update-level', (e) => {
      // set maxOptimalZoom
      if (e.level === dziHighestLevel) {
        if (!maxOptimalZoom) {
          const maxOptimalZoom = viewerBarcode.viewport.getZoom()
          viewerBarcode.maxOptimalZoom = maxOptimalZoom
          setMaxOptimalZoom(maxOptimalZoom)
        }
      }
    })
    // On viewer_barcode double click
    viewerBarcode.addHandler('canvas-double-click', (e) => {
      setSplitSizes([50, 50])
      if (e.originalEvent.target.classList.contains('libraryTagDeleteButton')) {
        viewerBarcode.zoomPerClick = 1
        viewerBarcode.addOnceHandler('canvas-release', () => {
          viewerBarcode.zoomPerClick = 1.5
        })
        return
      }
    })

    // On viewer_barcode key
    viewerBarcode.addHandler('canvas-key', canvasKeyHandler)
  }

  const canvasInitialHandler = (e) => {
    setEventObject(e)
  }

  /**
   * @function canvasKeyHandler
   *@desc Handler for key press when the viewer is focused - arrow keys control custom pan movement by a given increment. That increment is set in options and will eventually be configurable per user.
   * @returns {void}
   */
  const canvasKeyHandler = (e) => {
    e.preventDefaultAction = true
    e.preventVerticalPan = true
    e.preventHorizontalPan = true

    // TODO: make this configurable via redis
    const panByPercentage = 1 // maximize the percentage when there are more tiles
    const key = e.originalEvent.key || e.originalEvent.keyCode
    const viewerWidthPixels = e.eventSource.container.getBoundingClientRect().width
    const viewerHeightPixels = e.eventSource.container.getBoundingClientRect().height
    if (key === 'ArrowRight' || key === 39) {
      // Right arrow key
      if (e.eventSource.viewport.getZoom() > 2) {
        e.eventSource.viewport.zoomTo(e.eventSource.viewport.minZoomLevel)
      }
      e.eventSource.viewport.panBy(
        e.eventSource.viewport.deltaPointsFromPixels(new OpenSeadragon.Point(viewerWidthPixels * panByPercentage, 0)),
      )
      // Left arrow key
    } else if (key === 'ArrowLeft' || key === 37) {
      if (e.eventSource.viewport.getZoom() > 2) {
        e.eventSource.viewport.zoomTo(e.eventSource.viewport.minZoomLevel)
      }
      e.eventSource.viewport.panBy(
        e.eventSource.viewport.deltaPointsFromPixels(
          new OpenSeadragon.Point(-(viewerWidthPixels * panByPercentage), 0),
        ),
      )
    } else if (key === 'ArrowUp' || key === 38) {
      e.eventSource.viewport.panBy(
        e.eventSource.viewport.deltaPointsFromPixels(
          new OpenSeadragon.Point(0, -(viewerHeightPixels * panByPercentage)),
        ),
      )
    } else if (key === 'ArrowDown' || key === 40) {
      e.eventSource.viewport.panBy(
        e.eventSource.viewport.deltaPointsFromPixels(new OpenSeadragon.Point(0, viewerHeightPixels * panByPercentage)),
      )
    }
    e.eventSource.viewport.applyConstraints()
  }

  /**
   * @function createPremerisOverlays
   * @desc Creates premeris overlays for toggling on and off red/blue rects on whole slide
   * @returns {void}
   */
  const createPremerisOverlays = () => {
    const overlays = {}
    wholeSlideRects.forEach((rect) => {
      const osdRect = viewerBarcode.viewport.imageToViewportRectangle(
        rect.slideRect.xmin,
        rect.slideRect.ymin,
        rect.slideRect.width,
        rect.slideRect.height,
      )
      overlays[rect.clid] = osdRect
    })
    setAllPremerisOverlays(overlays)
  }

  /**
   * @function addViewerClassifierHandlers
   * @desc Adds event handlers to viewer_classifier
   * @returns {void}
   */
  const addViewerClassifierHandlers = () => {
    // Add null check at the beginning
    if (!viewerClassifier) {
      console.error('Cannot add classifier handlers: viewerClassifier is null')
      return
    }

    // On viewer_classifier open failed
    viewerClassifier.addHandler('open-failed', (e) => {
      setViewerClassifierLoading(false)
      // Set error message if open fails
      setViewerData({
        ...viewerData,
        classifierError: {
          message: 'Sorry, the image is not available.',
        },
      })
    })

    // On viewer_classifier open
    viewerClassifier.addHandler('open', (e) => {
      setViewerClassifierLoading(false)

      // Only clear error if we know file exists
      if (viewerData.classifierFileExists) {
        setViewerData({
          ...viewerData,
          classifierError: null,
        })
      }

      viewerClassifier.viewport.fitVertically()
      viewerClassifier.viewport.minZoomLevel = viewerClassifier.viewport.getZoom()
      const currentBounds = viewerClassifier.viewport.getBounds()
      const contentBounds = viewerClassifier.viewport._contentBounds
      const newBoundsValue = new OpenSeadragon.Rect(0, contentBounds.y, currentBounds.width, contentBounds.height)
      viewerClassifier.viewport.fitBounds(newBoundsValue, true)
      // grab the current zoom level and save it for future zoom logic purposes
      setHomeZoom(viewerClassifier.viewport.getZoom())

      // Update the go home function to reset home to new bounds
      // viewerClassifier.viewport.goHome(true)
      viewerClassifier.viewport.goHome = (immediately) => {
        if (viewerClassifier) {
          viewerClassifier.raiseEvent('home', {
            immediately,
          })
          viewerClassifier.viewport.fitBounds(newBoundsValue, immediately)
        }
        // Set maxZoom of viewer_classifier
        // viewerClassifier.viewport.maxZoomLevel = Infinity
        // Call goHome on viewer_classifier - does not work without timeout
        setTimeout(() => {
          viewerClassifier.viewport.goHome(true)
        }, 250)
      }
      if (isRefreshClicked) {
        viewerClassifier.viewport.panTo({ x: prevClassifierPoint.x, y: prevClassifierPoint.y })
        viewerClassifier.viewport.zoomTo(prevClassifierZoom)
      } else {
        const center = viewerClassifier.viewport.getBounds(true).getCenter()
        viewerClassifier.viewport.panTo({ x: center.x, y: center.y })
      }
    })

    // On viewer_classifier zoom
    viewerClassifier.addHandler('zoom', (e) => {
      if (e.zoom > homeZoom) {
        viewerClassifier.panVertical = true
      } else if (e.zoom <= homeZoom) {
        viewerClassifier.panVertical = false
      }
    })

    // On viewer_classifier resize
    viewerClassifier.addHandler('resize', () => {
      viewerClassifier.viewport.fitVertically()

      viewerClassifier.viewport.minZoomLevel = viewerClassifier.viewport.getZoom()

      setTimeout(() => {
        viewerClassifier.viewport.applyConstraints(true)
      }, 10)
    })

    // Contextmenu event for viewerClassifier element - do we need this?
    if (viewerClassifier.element) {
      viewerClassifier.element.addEventListener('contextmenu', (e) => {
        e.preventDefault()
        viewerClassifier.viewport.zoomTo(viewerClassifier.viewport.minZoomLevel)
      })
    }

    // On viewer_classifier double click
    viewerClassifier.addHandler('canvas-double-click', (e) => {
      setSplitSizes([80, 20])
      classifierCanvasClickHandler(e)
    })

    // On viewer_classifier click
    viewerClassifier.addHandler('canvas-click', (e) => {
      classifierCanvasClickHandler(e)
    })

    // On viewer_classifier canvas key
    viewerClassifier.addHandler('canvas-key', canvasKeyHandler)
    viewerClassifier.addHandler('viewport-change', canvasInitialHandler)
  }

  /**
   * @function setNewBounds
   * @desc Fits the image vertically within viewport, and then sets x position to 0. Called every time a new tileSource is opened
   * @returns {void}
   */
  // const setNewBounds = () => {
  //   // Set starting x position to 0 on viewer_classifier
  //   viewerClassifier.viewport.fitVertically()

  //   const currentBounds = viewerClassifier.viewport.getBounds()
  //   const contentBounds = viewerClassifier.viewport._contentBounds
  //   const newBoundsValue = new OpenSeadragon.Rect(0, contentBounds.y, currentBounds.width, contentBounds.height)

  //   viewerClassifier.viewport.fitBounds(Object.keys(newBounds).length ? newBounds : newBoundsValue, true)
  //   // grab the current zoom level and save it for future zoom logic purposes
  //   setHomeZoom(viewerClassifier.viewport.getZoom())
  //   setNewBoundsValue(newBoundsValue)
  // }

  /**
   * @function classifierCanvasClickHandler
   * @desc Handles click on Premeris View - navigates to same region on viewer_barcode
   * @param e event
   * @returns {void}
   */
  const classifierCanvasClickHandler = async (e) => {
    const slideBarcode = currentSlideBarcode
    if (!effectiveClassifierResponsefilter[0] || !effectiveBarcodeData.kpInstance) {
      console.error('Missing required data for classifier canvas click handler')
      return
    }

    const showingWhichPremerisFile = 'reg'
    const webPoint = e.position
    const viewportPoint = viewerClassifier.viewport.pointFromPixel(webPoint)
    const imagePoint = viewerClassifier.viewport.viewportToImageCoordinates(viewportPoint.x, viewportPoint.y)

    const apiName = `/premeris/slideBarcodeInfo/${slideBarcode}/premeris/${imagePoint.x}/${imagePoint.y}/${showingWhichPremerisFile}/${effectiveBarcodeData.kpInstance}/${effectiveClassifierResponsefilter[0].version}`
    const options = {
      headers: {},
      response: false,
    }

    try {
      const result = await API.get(WOMA_API_GATEWAY_NAME, apiName, options)
      const { clid, regOrBlue, slideRect } = result

      if (!clid) return

      const rect = viewerBarcode.viewport.imageToViewportRectangle(
        slideRect.xmin,
        slideRect.ymin,
        slideRect.width,
        slideRect.height,
      )

      let rectOverlay = null

      viewerBarcode.currentOverlays.forEach((overlay) => {
        const id = `${overlay.element.id}`
        if (id === clid.toString()) {
          rectOverlay = premerisOverlays[clid]
          // eslint-disable-next-line
          return
        }
      })

      if (!rectOverlay) {
        const overlay = makeWholeSlideOverlay(clid.toString(), regOrBlue, stain)

        overlay.addEventListener('double-click', (e) => {
          wholeSlideOverlayClickListener(e)
        })

        viewerBarcode.addOverlay({
          element: overlay,
          location: rect,
          rotationMode: OpenSeadragon.OverlayRotationMode.BOUNDING_BOX,
        })

        premerisOverlays[clid] = rect
        setAllPremerisOverlays(premerisOverlays)

        rectOverlay = rect
      }

      const rectCenter = rectOverlay.getCenter()
      viewerBarcode.viewport.panTo(rectCenter)

      determineContextZoomLevel(slideRect, regOrBlue, clid)
    } catch (error) {
      console.error('Error in classifier canvas click handler:', error)
    }
  }

  /**
   * @function determineContextZoomLevel
   * @desc Determines best zoom level based on how large the given premeris rect is. Used in conjuction with classifierCanvasClickHandler.
   * @param rect premeris rect dimensions, in image coords.
   * @returns {void}
   */
  const determineContextZoomLevel = (rect, regOrBlue, clid) => {
    // determine if rect's width, height are too large compared to viewport bounds
    const viewerWidth = viewerBarcode.element.getBoundingClientRect().width
    const viewerHeight = viewerBarcode.element.getBoundingClientRect().height
    const viewerArea = viewerWidth * viewerHeight

    const rectImgWidth = rect.width
    const rectImgHeight = rect.height
    const rectArea = rectImgWidth * rectImgHeight

    if (viewerArea < 750000) {
      const percent = (rectArea / viewerArea) * 100
      let newZoom
      // rect is bigger than viewer
      if (percent > 100) {
        newZoom = 100
        // rect is smaller than viewer
      } else {
        newZoom = 200
      }
      viewerBarcode.viewport.zoomTo(newZoom)
    } else {
      const percent = (rectArea / viewerArea) * 100
      let newZoom
      // rect is bigger than viewer
      if (percent > 100) {
        newZoom = 40
        // rect is smaller than viewer
      } else {
        newZoom = 100
      }
      viewerBarcode.viewport.zoomTo(newZoom)
    }

    setShowingRectangle(regOrBlue, clid)
  }
  /**
   * @function handleSplitSizesChange
   * @desc Handles double click event on divs that surround SplitJS gutter. For changing SplitJS's sizes.
   * @param {event} e the double click event (from which the function uses e.target)
   * @returns {void}
   */
  const handleSplitSizesChange = (e) => {
    const className = e.target.classList[0].toLowerCase()
    if (className.match('left')) {
      setSplitSizes([80, 20])
    } else if (className.match('gutter')) {
      setSplitSizes([50, 50])
    } else {
      setSplitSizes([50, 50])
    }
  }

  /**
   * @function configureCustomSplitGutters
   * @desc Creates and appends custom left and right gutters for double click behavior in SplitJS
   * @returns {void}
   */
  const configureCustomSplitGutters = () => {
    const gutter = document.getElementsByClassName('gutter')[0]
    gutter.addEventListener('dblclick', handleSplitSizesChange)
  }

  const handleRightClick = (e) => {
    if (viewerClassifier.viewport.getZoom() > 2) {
      eventObject.eventSource.viewport.zoomTo(eventObject.eventSource.viewport.minZoomLevel)
    }
    eventObject.preventDefaultAction = true
    eventObject.preventVerticalPan = true
    eventObject.preventHorizontalPan = true
    const panByPercentage = 1 // maximize the percentage when there are more tiles
    const viewerWidthPixels = eventObject.eventSource.container.getBoundingClientRect().width
    eventObject.eventSource.viewport.panBy(
      eventObject.eventSource.viewport.deltaPointsFromPixels(
        new OpenSeadragon.Point(viewerWidthPixels * panByPercentage, 0),
      ),
    )
    eventObject.eventSource.viewport.applyConstraints()
  }

  const handleLeftClick = (e) => {
    if (viewerClassifier.viewport.getZoom() > 2) {
      eventObject.eventSource.viewport.zoomTo(eventObject.eventSource.viewport.minZoomLevel)
    }
    eventObject.preventDefaultAction = true
    eventObject.preventVerticalPan = true
    eventObject.preventHorizontalPan = true
    const panByPercentage = 1 // maximize the percentage when there are more tiles
    const viewerWidthPixels = eventObject.eventSource.container.getBoundingClientRect().width
    eventObject.eventSource.viewport.panBy(
      eventObject.eventSource.viewport.deltaPointsFromPixels(
        new OpenSeadragon.Point(-(viewerWidthPixels * panByPercentage), 0),
      ),
    )
    eventObject.eventSource.viewport.applyConstraints()
  }

  const handleCenterClick = () => {
    setSplitSizes([50, 50])
  }

  const handleExpandClick = () => {
    setSplitSizes([80, 20])
  }

  /**
   * @function wholeSlideOverlayClickListener
   * @desc Handles click on whole slide overlays. Navigates to correct premeris tile.
   * @param {event} e the click event (from which the function uses e.target.id)
   * @returns {void}
   */
  const wholeSlideOverlayClickListener = (e) => {
    // TODO: fix this for when showing blue premeris
    const premerisRectLocations = []
    // choose a number for ratio of img height / rect height
    const rectHeight = 35
    const imgDims = viewerClassifier.world.getItemAt(0).getContentSize()
    // add addToSummary overlay to each Premeris rect on Premeris view
    // TODO: ensure that this data exists here before this function is run
    if (premerisRectLocationsFetched) {
      premerisRectLocationsFetched.forEach((rect) => {
        const rectOverlay = viewerClassifier.viewport.imageToViewportRectangle(
          rect.x + 10,
          rect.y + 20,
          imgDims.y / rectHeight,
          imgDims.y / rectHeight,
        )
        const overlay = makeClassifierAddToSummaryOverlay(rect.clid)
        // OverlayHelper.addEventsToAddToSummaryOverlay(overlay);
        viewerClassifier.addOverlay(overlay, rectOverlay)
        const obj = {}
        obj.clid = rect.clid
        obj.rect = {
          x: rect.x,
          y: rect.y,
          width: rect.w,
          height: rect.h,
        }
        premerisRectLocations.push(obj)
      })
    }
    premerisRectLocations.sort((a, b) => {
      return a.order - b.order
    })
    let listToIterate
    let clid
    let id
    if (!e.target.id) {
      id = e.target.parentElement.id
    } else {
      id = e.target.id
    }
    listToIterate = premerisRectLocations
    clid = id
    const resultLocation = listToIterate.filter((location) => {
      return location.clid === Number(clid)
    })[0].rect

    const osdRect = viewerClassifier.viewport.imageToViewportRectangle(
      resultLocation.x,
      resultLocation.y,
      resultLocation.width,
      resultLocation.height,
    )
    const osdRectCenter = osdRect.getCenter()
    const premerisOverlay = makePremerisMovieOverlay()

    viewerClassifier.addOverlay(premerisOverlay, osdRect)
    setTimeout(() => {
      viewerClassifier.removeOverlay(premerisOverlay)
    }, 3000)
    viewerClassifier.viewport.panTo(osdRectCenter)
    viewerClassifier.viewport.applyConstraints()
  }

  const applyStylingFilterValues = (viewerInstance) => {
    if (viewerInstance !== null && viewerInstance.viewport !== null) {
      viewerInstance.viewport.viewer.element.style.filter =
        'brightness(' + imgFilterValue.brightness + '%)' + ' contrast(' + imgFilterValue.contrast + '%)'
    }
  }

  const calculateGammaFilter = (value) => {
    if (value < 100) {
      return 1 + value / 100
    } else if (value > 100) {
      return 1 - (value - 100) / 100
    } else {
      return 1
    }
  }

  const applyGammaFilterValues = (viewerInstance) => {
    if (viewerInstance !== null && viewerInstance.viewport !== null) {
      const calcGammaR = calculateGammaFilter(imgFilterValue.gammaR)
      const calcGammaG = calculateGammaFilter(imgFilterValue.gammaG)
      const calcGammaB = calculateGammaFilter(imgFilterValue.gammaB)
      const calcSharpness = imgFilterValue.sharpness //calculateGammaFilter(imgFilterValue.sharpness)

      viewerInstance.gammaCorrection(calcGammaR, calcGammaG, calcGammaB, calcSharpness)
    }
  }

  const handleStyleImageFilters = () => {
    applyStylingFilterValues(viewerBarcode) //Apply RHS
    applyStylingFilterValues(viewerClassifier) //Apply LHS
  }

  const handleGammaImageFilters = () => {
    applyGammaFilterValues(viewerBarcode) //Apply RHS
    applyGammaFilterValues(viewerClassifier) //Apply LHS
  }

  const getWidth = () => {
    if (splitSizes[0] < 70) {
      return '50%'
    } else if (splitSizes[0] > 70) {
      return '78%'
    }
  }

  const getMarginRight = () => {
    if (splitSizes[0] < 70) {
      return '55px'
    } else if (splitSizes[0] > 70) {
      return '45px'
    }
  }

  const getMarginTop = () => {
    if (splitSizes[0] < 70) return 135
    else if (splitSizes[0] > 70) return 110
  }

  return (
    <>
      {viewerClassifierLoading && <Loader />}
      <div style={{ marginTop: '-80px', float: 'right', marginRight: '210px' }}>
        <fieldset
          style={{
            borderRadius: '5px',
            padding: '5px 5px 5px 5px',
            height: '60px',
            marginTop: '10px',
            color: 'rgba(0,0,0,0.6)',
          }}
        >
          <legend style={{ fontSize: '12.5px' }}>Image Tools</legend>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '5px',
              marginTop: '6px',
              paddingTop: '5px',
            }}
          >
            <div style={{ display: 'inline-block' }}>
              <Button
                variant="outlined"
                disableElevation
                onClick={handleLeftClick}
                className="osdButtons"
                style={{ marginRight: '8px' }}
              >
                <Tooltip title="Move classifier image left" placement="top-start">
                  <KeyboardArrowLeftIcon />
                </Tooltip>
              </Button>
              <Button
                variant="outlined"
                disableElevation
                onClick={handleRightClick}
                className="osdButtons"
                style={{ marginRight: '8px' }}
              >
                <Tooltip title="Move classifier image right" placement="top-start">
                  <KeyboardArrowRightIcon />
                </Tooltip>
              </Button>
              <Button
                variant="outlined"
                disableElevation
                onClick={handleCenterClick}
                className="osdButtons"
                style={{ marginRight: '8px' }}
              >
                <Tooltip title="Align images in center" placement="top-start">
                  <ComputerOutlinedIcon />
                </Tooltip>
              </Button>
              <Button
                variant="outlined"
                disableElevation
                onClick={handleExpandClick}
                className="osdButtons"
                style={{ marginRight: '8px' }}
              >
                <Tooltip title="Enlarge classifier image" placement="top-start">
                  <ExitToAppOutlinedIcon />
                </Tooltip>
              </Button>
              <Button variant="outlined" disableElevation onClick={handleRefreshClick} className="osdButtons">
                <Tooltip title="Reload images" placement="top-start">
                  <RefreshIcon />
                </Tooltip>
              </Button>
            </div>
            <div
              style={{
                display: 'inline-block',
                height: '36px',
                paddingLeft: '5px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ImageFilter imgFilterValue={imgFilterValue} setImgFilterValue={setImgFilterValue} stain={stain} />
            </div>
          </div>
        </fieldset>
      </div>
      <Split
        sizes={splitSizes}
        style={{
          height: 'calc(100vh - 11em)',
          width: '100vw',
          display: 'flex',
          cursor: 'grab',
          position: 'absolute',
          //top: '11em',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          {filesChecked && !viewerData.classifierFileExists ? (
            <div style={{ width: '100%', height: '100%' }}>
              <ViewerError message={'Sorry, Classifier Image is not available'} />
            </div>
          ) : (
            <>
              <div
                id="viewerClassifier"
                className={viewerClassifierLoading ? 'openseadragon_left_hidden' : 'openseadragon_left'}
                style={{ width: '100%', height: '100%' }}
              ></div>
              {viewerData.classifierError && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1000,
                  }}
                >
                  <ViewerError message={'Sorry, Classifier Image is not available'} />
                </div>
              )}
            </>
          )}
        </div>
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          {filesChecked && !viewerData.wsiFileExists ? (
            <div style={{ width: '100%', height: '100%' }}>
              <ViewerError message={'Sorry, the Whole Slide Image is not available'} />
            </div>
          ) : (
            <>
              <div
                id="viewerBarcode"
                className={viewerBarcodeLoading ? 'openseadragon_right_hidden' : 'openseadragon_right'}
                style={{ width: '100%', height: '100%' }}
              ></div>
              {viewerData.wsiError && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1000,
                  }}
                >
                  <ViewerError message={'Sorry, the Whole Slide Image is not available'} />
                </div>
              )}
            </>
          )}
        </div>
      </Split>
      {!viewerData.wsiError && filesChecked && viewerData.wsiFileExists && <Scalebar viewer={viewerBarcode} />}
    </>
  )
}
